import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Award, Printer, ArrowLeft, Copy, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabaseClient';
import BrandMark from '../components/BrandMark';

interface Cert {
  verification_code: string;
  recipient_name: string;
  skill_path_title: string;
  skill_path_slug: string;
  issued_at: string;
}

export default function CertificatePage() {
  const { code } = useParams();
  const [cert, setCert] = useState<Cert | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code) return;
    (async () => {
      const { data } = await supabase
        .rpc('get_certificate_by_code', { p_code: code })
        .maybeSingle();
      setCert(data);
      setLoading(false);
    })();
  }, [code]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-warm-50 text-ink-500 text-sm">Loading...</div>;
  }
  if (!cert) {
    return (
      <div className="min-h-screen grid place-items-center bg-warm-50 px-6">
        <div className="text-center">
          <h1 className="font-display text-3xl mb-2">Certificate not found</h1>
          <Link to="/" className="btn-primary mt-3">Back home</Link>
        </div>
      </div>
    );
  }

  const issued = new Date(cert.issued_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const certUrl = `${window.location.origin}/certificates/${cert.verification_code}`;
  const issueDate = new Date(cert.issued_at);
  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certUrl)}`;
  const linkedInAddUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(cert.skill_path_title)}&organizationName=PromptGPT&certUrl=${encodeURIComponent(certUrl)}&issueYear=${issueDate.getFullYear()}&issueMonth=${issueDate.getMonth() + 1}`;

  return (
    <div className="min-h-screen bg-warm-50">
      <header className="bg-white border-b border-ink-100 print:hidden">
        <div className="container-app py-4 flex items-center justify-between">
          <Link to="/" aria-label="PromptGPT home"><BrandMark tone="dark" size={24} /></Link>
          <div className="flex items-center gap-2">
            <Link to={`/skill-paths/${cert.skill_path_slug}`} className="btn-secondary text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to path
            </Link>
            <button onClick={() => window.print()} className="btn-primary text-sm">
              <Printer className="w-4 h-4" /> Print or save PDF
            </button>
          </div>
        </div>
      </header>

      <div className="container-app py-12 print:py-0">
        <div className="max-w-3xl mx-auto bg-white border border-ink-200 rounded-2xl p-12 md:p-16 shadow-soft print:shadow-none print:border-0">
          <div className="flex items-center justify-between mb-12">
            <BrandMark tone="dark" size={28} />
            <Award className="w-8 h-8 text-accent-600" />
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-ink-500 font-semibold mb-3">Certificate of completion</div>
          <p className="text-ink-600 mb-2">This certifies that</p>
          <h1 className="font-display text-5xl md:text-6xl mb-6 leading-tight">{cert.recipient_name}</h1>
          <p className="text-ink-600 mb-2">has successfully completed</p>
          <h2 className="font-display text-3xl md:text-4xl text-accent-700 mb-12">{cert.skill_path_title}</h2>
          <div className="grid grid-cols-2 gap-8 pt-8 border-t border-ink-100">
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-1">Issued on</div>
              <div className="text-ink-800">{issued}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-500 font-semibold mb-1">Verification code</div>
              <div className="text-ink-800 font-mono text-sm break-all">{cert.verification_code}</div>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-ink-500 mt-4 print:hidden">
          Verify this certificate at {certUrl}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2 mt-6 print:hidden">
          <button
            onClick={() => {
              navigator.clipboard.writeText(certUrl);
              toast.success('Link copied to clipboard');
            }}
            className="btn-secondary text-sm"
          >
            <Copy className="w-4 h-4" /> Copy link
          </button>
          <a
            href={linkedInShareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-sm"
          >
            <Linkedin className="w-4 h-4" /> Share on LinkedIn
          </a>
          <a
            href={linkedInAddUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-sm"
          >
            <Linkedin className="w-4 h-4" /> Add to LinkedIn profile
          </a>
        </div>
      </div>
    </div>
  );
}
